"use client"

import { useState, useEffect, type ChangeEvent } from "react"
import { Category, User } from "../../../lib/types"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { FolderOpen, ChevronRight, Trash2, ViewIcon, User2Icon, Share, Camera } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { SelectUsersModal } from "./select-users-modal"
import { supbaseManager, getUserId } from "../../../API"
import { Share2Icon } from "@radix-ui/react-icons"
import { Dialog, DialogTrigger, } from "../../../components/ui/dialog";
import {
  OptionsControlModal,
  defaultConfig,
  addColorOptions,
  createColorOption,
  type OptionsConfig,
  type ColorOption,
} from "../options-control-modal";
import type { ColorTable } from "../../../lib/types";

// Dados de exemplo para demonstração
const SAMPLE_TABLES: ColorTable[] = [];

// Função que será chamada quando salvar a configuração
function saveConfiguration(config: OptionsConfig) {
  const jsonConfig = JSON.stringify(config, null, 2)

  // Aqui você pode implementar a lógica para salvar no banco de dados
  // Por exemplo: fetch('/api/save-config', { method: 'POST', body: jsonConfig })

  alert("Configuração salva! Verifique o console para ver o JSON.")
}

// Cores padrão do modal
const defaultColors: ColorOption[] = []

// Exemplo: Adicionando novas cores personalizadas
var customColors = addColorOptions(defaultColors, [])

interface CategoryListProps {
  users: User[]
  categories: Category[]
  onSelectCategory: (category: Category) => void
  onDeleteCategory: (categoryId: string) => void
  onUpdateCategory: (category: Category) => void // 👈 novo
  onUpdateCategoryThumbnail: (category: Category, dataUrl: string) => void
}

export function CategoryList({ users, categories, onSelectCategory, onDeleteCategory, onUpdateCategory, onUpdateCategoryThumbnail }: CategoryListProps) {
  const [selectModalOpen, setSelectModalOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState<Category | null>(null) as any

  const [tables, setTables] = useState<ColorTable[]>(SAMPLE_TABLES)

  const [open, setOpen] = useState(false)
  const [savedConfig, setSavedConfig] = useState<OptionsConfig>(defaultConfig)

  //
    useEffect(() => {
      loadTables();
    }, []);

     const loadTables = async () => {
      try {
        const res = await supbaseManager("get", "color-tables", null, getUserId())
  
        if (res.length == 0) {
          setTables([])
          return
        }
  
        var res2 = [] as any;
  
        for (var i = 0; i < res.length; i++) {
          const content = JSON.parse(res[i].content);
          content.id = res[i].id.toString();
          res2.push(content);
        }
  
        // ⚠️ importante: converter datas
        const parsed = res2.map((t: ColorTable) => ({
          ...t,
          createdAt: new Date(t.createdAt),
          updatedAt: new Date(t.updatedAt),
        }));
  
        var array = [] as any;
  
        for (var i = 0; i < parsed.length; i++) {
          array.push(createColorOption(parsed[i].id, parsed[i].name));
        }
  
        customColors = addColorOptions([], array)
  
        setTables(parsed)
      } catch (err) {
        console.error(err)
      }
    }

  const handleSave = (config: OptionsConfig, cat: any) => {

    (window as any).projectSharedID = getUserId() + "&s=" + cat.id;

    setSavedConfig(config);
    saveConfiguration(config);

    /*const link = window.location.origin + "?i=" + localStorage.getItem("user_id") + "&s=" + cat.id;

    navigator.clipboard.writeText(link)
      .then(() => {
        (window as any)['toast']("Link copiado!", "info")
      })
      .catch(err => {
        console.error("Erro ao copiar: ", err);
      });*/
  }

  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground">Nenhuma categoria</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Clique em &quot;Nova Categoria&quot; para começar.
        </p>
      </div>
    )
  }

  const handleAddUsers = async (userIds: string[]) => {
    if (!activeCategory) return

    const updatedCategory: Category = {
      ...activeCategory,
      userIds, // 👈 substitui, não faz merge
    }

    if (window.confirm("Atualizar categoria?")) {
      await supbaseManager(
        "update",
        "categories_new",
        JSON.stringify(updatedCategory),
        updatedCategory.id
      )
    }

    onUpdateCategory(updatedCategory)
    setActiveCategory(updatedCategory)
    setSelectModalOpen(false)
  }

  const handleShareCategory = function (cat: any, edit: any) {
    const link = window.location.origin + "?i=" + getUserId() + "&s=" + cat.id + "&v=" + edit;

    navigator.clipboard.writeText(link)
      .then(() => {
        (window as any)['toast']("Link copiado!", "info")
      })
      .catch(err => {
        console.error("Erro ao copiar: ", err);
      });
  }

  const handleThumbnailFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    category: Category
  ) => {
    const file = e.target.files?.[0]
    e.target.value = "" // permite escolher o mesmo arquivo de novo depois

    if (!file) return

    if (!file.type.startsWith("image/")) {
      alert("Por favor, selecione apenas arquivos de imagem.")
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string
      onUpdateCategoryThumbnail(category, dataUrl)
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {categories.map((category) => (
        <Card
          key={Math.random()}
          className={`group cursor-pointer transition-all hover:shadow-md hover:border-primary/50
    ${category.shared ? 'bg-muted' : 'bg-muted'}
  `}
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <label
                  className="relative h-9 w-9 flex items-center justify-center cursor-pointer"
                  title="Trocar thumbnail"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="rounded-lg bg-primary/10 h-9 w-9 flex items-center justify-center overflow-hidden">
                    {(category as any).thumbnail ? (
                      <img
                        src={(category as any).thumbnail}
                        alt={category.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FolderOpen className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  {/* Badge sempre visível (não depende de :hover) indicando que dá pra trocar a imagem */}
                  <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary flex items-center justify-center border border-background">
                    <Camera className="h-2.5 w-2.5 text-primary-foreground" />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleThumbnailFileChange(e, category)}
                  />
                </label>
                <div>
                  <CardTitle className="text-base">{category.name}</CardTitle>
                  <CardDescription className="text-xs">
                    {category.projectIds.length} projeto(s)
                  </CardDescription>
                </div>
              </div>
              <Button
                title="GERENCIAR"
                //key={category.id}
                variant="ghost"
                size="icon"
                className="h-8 w-[50px] transition-opacity bg-background p-1 hover:bg-primary hover:text-background"
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveCategory(category)
                  setSelectModalOpen(true)
                  onSelectCategory(category)
                }}
              >
                <ViewIcon className="h-4 w-4"></ViewIcon>
              </Button>

              <div>
                <Button
                  onClick={() => {
                    setOpen(true);
                    //(window as any).projectSharedID = localStorage.getItem("user_id") + "&s=" + category.id;
                    (window as any).categoryShared = getUserId() + "&s=" + category.id;
                    (window as any).projectSharedID = category.id;
                  }}
                  size="sm"
                  className="gap-2 view-share bg-background text-primary hover:text-background"
                >
                  <Share className="h-4 w-4" />
                </Button>
                <OptionsControlModal
                  open={open}
                  onOpenChange={setOpen}
                  initialConfig={savedConfig}
                  onSave={(value) => handleSave(value, category)}
                  colorOptions={customColors}
                />
              </div>

              {getUserId() == "7facba1d-3dc7-4d41-ab47-759019e136af" ?
                <Button
                  title="USUÁRIOS"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-[50px] transition-opacity bg-background p-1 hover:bg-primary hover:text-background"
                  onClick={(e) => {
                    e.stopPropagation()
                    setActiveCategory(category)
                    setSelectModalOpen(true)
                  }}
                >
                  <User2Icon className="h-4 w-4"></User2Icon>
                </Button> : null
              }

              {!category.shared ?
                <Button
                  title="APAGAR"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-[50px] opacity-1 group-hover:opacity-100 transition-opacity bg-background hover:bg-primary hover:text-background p-1 text-destructive"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteCategory(category.id)
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Excluir categoria</span>
                </Button> : null
              }
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="text-xs">
                {category.projectIds.length === 0
                  ? "Sem projetos"
                  : `${category.projectIds.length} projeto${category.projectIds.length > 1 ? "s" : ""}`}
              </Badge>

              {category.shared ?
                <Badge className="bg-[orange]">Categoria ADM</Badge> : <Badge className="bg-[blue] text-white">Minha Categoria</Badge>
              }

              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </CardContent>
        </Card>
      ))}

      <SelectUsersModal
        open={selectModalOpen}
        onOpenChange={setSelectModalOpen}
        allUsers={users}
        selectedUserIds={activeCategory?.userIds ?? []}
        currentCategory={activeCategory}
        onConfirm={handleAddUsers}
      />
    </div>
  )
}